/* eslint-disable @typescript-eslint/no-loop-func */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-await-in-loop */
import { useMemo, useState } from "react";

import Stack from "@mui/material/Stack";
import { BarProgress, Typography, CommonDialog } from "components";
import groupBy from "lodash/groupBy";
import isNumber from "lodash/isNumber";
import { useDialog } from "stores/Dialog";
import i18n from "translations/i18n";
import { getValueEmptyInExcel } from "utils";
import { rem } from "utils/styling";
import {
  getCellWithStyle,
  DataType,
  getHeightsXlsx,
  getMergesXlsx,
} from "utils/xlsx";
import { CellObject, utils, writeFile } from "xlsx-js-style";

const PAGE_DEFAULT = 1;

export type ConfigFieldsExport = {
  [e: string]: {
    name: string;
    order: number;
    dataType?: DataType;
    align?: "left" | "right" | "center";
    formatter?: (record: any) => string | number;
  };
};

const generateHeader = ({
  configs,
  bgColor = "B74C27",
  color = "FFFFFF",
  border = false,
  bold = true,
  initValue,
}: {
  configs: ConfigFieldsExport;
  bgColor?: string;
  color?: string;
  border?: boolean;
  bold?: boolean;
  initValue?: CellObject[];
}) => {
  const headers: CellObject[] = initValue || [];

  Object.values(configs)
    .sort((a, b) => a.order - b.order)
    .forEach((config) => {
      headers.push(
        getCellWithStyle({
          value: config.name,
          bold,
          bgColor,
          color,
          noBorder: !border,
          textAlign: "center",
        })
      );
    });

  return [headers];
};

function round(value: number, precision = 0) {
  const multiplier = 10 ** precision;
  return Math.round(value * multiplier) / multiplier;
}

const useExportExcel = <
  TItem,
  TRequest,
  TResponse extends RestAPIResponse<{ items: TItem[]; total?: number }>
>({
  serviceFn,
  filters,
  limit = 500,
  configs,
  fileName,
  colWidth = 20,
  sheetName = "",
  titleModal = "",
  useSummary,
  transformData,
}: {
  serviceFn: (e: TRequest) => Promise<TResponse>;
  filters: TRequest & {
    page?: number | null;
    limit?: number | null;
    isExport?: boolean;
  };
  limit?: number;
  configs?: ConfigFieldsExport;
  fileName: string;
  sheetName?: string;
  titleModal?: string;
  colWidth?: number;
  useSummary?: boolean;
  transformData?: (e: any) => any;
}) => {
  const [loading, setLoading] = useState(false);
  const [percent, setPercent] = useState(0);
  const [error, setError] = useState(false);
  const { onToggle } = useDialog({ name: "ExportDialog" });

  const convertDataToCellObject = ({
    title,
    items,
    startRowIndex,
    configsSort,
    summary,
    bgColor,
  }: {
    title: string;
    items: any[];
    startRowIndex: number;
    summary: any[];
    bgColor?: string;
    configsSort: [
      string,
      {
        name: string;
        order: number;
        dataType?: DataType;
        align?: "left" | "right" | "center";
        formatter?: (record: any) => string | number;
      }
    ][];
  }) => {
    let itemGroups = items;
    let rowIndex = startRowIndex;
    const currentSummary: any[] = [];
    let results: CellObject[][] = [];
    if (itemGroups?.length) {
      if (typeof transformData === "function") {
        itemGroups = transformData(itemGroups) as TItem[];
      }
      itemGroups.forEach((element: any) => {
        let row: CellObject[] = [];
        const overrideElement = {
          ...element,
          stt: rowIndex,
        };
        rowIndex += 1;

        if (configs) {
          row = [
            ...row,
            ...configsSort.map(([key, value], index: number) => {
              if (value.dataType === DataType.Number) {
                currentSummary[index] =
                  // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                  (currentSummary[index] || 0) +
                  Number(overrideElement?.[key] || 0);
              } else {
                currentSummary[index] = "";
              }
              if (value.formatter) {
                return getCellWithStyle({
                  value: value.formatter(overrideElement),
                  dataType: value.dataType,
                  noBorder: false,
                  textAlign: value.align || "left",
                });
              }
              return getCellWithStyle({
                value: String(overrideElement?.[key] || ""),
                dataType: value.dataType,
                noBorder: false,
                textAlign: value.align || "left",
              });
            }),
          ];
        }
        results.push(row);
      });
      // handle currentSummary
      results = [
        [
          ...((currentSummary
            ?.map((element, index: number) => {
              if (index === 1)
                return getCellWithStyle({
                  value: title,
                  dataType: DataType.String,
                  noBorder: false,
                  bold: true,
                  textAlign: "left",
                  bgColor,
                  wrapText: false,
                });
              if (element === "") {
                return getCellWithStyle({
                  value: "",
                  noBorder: false,
                  bgColor,
                });
              }
              return getCellWithStyle({
                value: String(round(element as never, 1) || ""),
                dataType: DataType.Number,
                noBorder: false,
                bold: true,
                bgColor,
              });
            })
            ?.filter(Boolean) as any) || []),
        ],
        ...results,
      ];
    }
    // total Summary
    const totalSummary = currentSummary?.map((element, index: number) => {
      if (isNumber(element)) {
        return +(element + +(summary[index] || 0));
      }
      return "";
    });
    return {
      results,
      summary: totalSummary,
      rowIndex,
    };
  };

  const fetchAllData = async ({
    groupField,
  }: {
    startRowData?: number;
    groupField?: {
      value: string;
      label: string;
      nested: {
        value: string;
        label: string;
      };
    };
  }) => {
    try {
      const merges: string[][] = [];
      let hasMore = true;
      let page = PAGE_DEFAULT;
      let startRowIndex = 1;
      let results: CellObject[][] = [];
      let summary: any[] = [];
      const configsSort = Object.entries(configs || []).sort(
        ([, a], [, b]) => a.order - b.order
      );
      if (groupField?.value) {
        let finalRes: any[] = [];
        while (hasMore) {
          const res = await serviceFn({
            ...filters,
            page,
            limit,
            ...(page === 1 && { isExport: true }),
          });
          if (res?.data?.items?.length < limit) {
            hasMore = false;
          }
          finalRes = [...finalRes, ...(res?.data?.items || [])];
          const totalRecords = res?.data?.total || 0;
          setPercent(Math.round((results.length / totalRecords) * 100) || 0);
          page += 1;
        }
        let groupByFirstField = groupBy(finalRes, groupField?.value);
        if (groupField?.nested?.value) {
          groupByFirstField = Object.entries(groupByFirstField).reduce(
            (acc: any, [key, value]) => {
              const groupBySecondField = groupBy(
                value,
                groupField?.nested?.value
              );
              acc[key] = groupBySecondField;
              // eslint-disable-next-line @typescript-eslint/no-unsafe-return
              return acc;
            },
            {}
          );
        }

        if (!groupField?.nested?.value) {
          Object.entries(groupByFirstField).forEach(
            ([valueGroupBy, itemGroups]) => {
              const cellsObject = convertDataToCellObject({
                items: itemGroups,
                startRowIndex,
                configsSort,
                title: getValueEmptyInExcel(valueGroupBy, "N/A"),
                summary,
                bgColor: "FFFF99",
              });
              results = [...results, ...(cellsObject?.results as never)];
              summary = cellsObject?.summary;
              startRowIndex = cellsObject?.rowIndex;
            }
          );
        }

        if (groupField?.nested?.value) {
          Object.entries(groupByFirstField).forEach(
            ([valueGroupBy, secondGroup]) => {
              let tempResult: any[] = [];
              let tempSummary: any[] = [];
              Object.entries(secondGroup).forEach(
                ([valueGroupBySecond, itemGroups]) => {
                  const cellsObject = convertDataToCellObject({
                    items: itemGroups,
                    startRowIndex,
                    configsSort,
                    title: getValueEmptyInExcel(valueGroupBySecond, "N/A"),
                    summary: tempSummary,
                    bgColor: "CCE5FF",
                  });
                  tempResult = [
                    ...tempResult,
                    ...(cellsObject?.results as never),
                  ];
                  tempSummary = cellsObject?.summary;
                  startRowIndex = cellsObject?.rowIndex;
                }
              );
              results = [
                ...results,
                tempSummary?.map((element, index) => {
                  if (index === 1) {
                    return getCellWithStyle({
                      value: getValueEmptyInExcel(valueGroupBy, "N/A"),
                      dataType: DataType.String,
                      wrapText: false,
                      noBorder: false,
                      bold: true,
                      textAlign: "left",
                      bgColor: "FFFF99",
                    });
                  }
                  if (element === "") {
                    return getCellWithStyle({
                      value: "",
                      noBorder: false,
                      bgColor: "FFFF99",
                    });
                  }
                  return getCellWithStyle({
                    value: String(round(element as never, 1) || ""),
                    dataType: DataType.Number,
                    noBorder: false,
                    bold: true,
                    bgColor: "FFFF99",
                  });
                }),
                ...(tempResult as never),
              ];
              summary = tempSummary?.map((element, index: number) => {
                if (isNumber(element)) {
                  return +(element + +(summary[index] || 0));
                }
                return "";
              });
            }
          );
        }

        if (useSummary) {
          const summaryRow: CellObject[] = [];
          summary.forEach((element, index) => {
            if (index === 0) {
              summaryRow.push(
                getCellWithStyle({
                  value: "",
                  dataType: DataType.String,
                  noBorder: false,
                  bold: true,
                  textAlign: "left",
                })
              );
            } else if (element === "") {
              summaryRow.push(
                getCellWithStyle({
                  value: "",
                  noBorder: false,
                })
              );
            } else {
              summaryRow.push(
                getCellWithStyle({
                  value: String(round(element as never, 1) || ""),
                  dataType: DataType.Number,
                  noBorder: false,
                  bold: true,
                })
              );
            }
          });
          results.push(summaryRow);
        }
      } else {
        while (hasMore) {
          const res = await serviceFn({
            ...filters,
            page,
            limit,
            ...(page === 1 && { isExport: true }),
          });
          if (res?.data?.items?.length < limit) {
            hasMore = false;
          }
          let items = res?.data?.items || [];

          if (items?.length) {
            if (typeof transformData === "function") {
              items = transformData(items) as TItem[];
            }
            items.forEach((element: any) => {
              if (configs) {
                results.push(
                  configsSort.map(([key, value], index: number) => {
                    if (value.dataType === DataType.Number) {
                      summary[index] =
                        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                        (summary[index] || 0) + Number(element?.[key] || 0);
                    } else {
                      summary[index] = "";
                    }
                    if (value.formatter) {
                      return getCellWithStyle({
                        value: value.formatter(element),
                        dataType: value.dataType,
                        noBorder: false,
                        textAlign: value.align || "left",
                      });
                    }
                    return getCellWithStyle({
                      value: String(element?.[key] || ""),
                      dataType: value.dataType,
                      noBorder: false,
                      textAlign: value.align || "left",
                    });
                  })
                );
              } else {
                results.push(element as never);
              }
            });
          }
          const totalRecords = res?.data?.total || 0;
          setPercent(Math.round((results.length / totalRecords) * 100) || 0);

          page += 1;
        }
        if (useSummary) {
          const summaryRow: CellObject[] = [];
          summary.forEach((element, index) => {
            if (index === 0) {
              summaryRow.push(
                getCellWithStyle({
                  value: "",
                  dataType: DataType.String,
                  noBorder: false,
                  bold: true,
                  textAlign: "left",
                })
              );
            } else if (element === "") {
              summaryRow.push(
                getCellWithStyle({
                  value: "",
                  noBorder: false,
                })
              );
            } else {
              summaryRow.push(
                getCellWithStyle({
                  value: String(element || ""),
                  dataType: DataType.Number,
                  noBorder: false,
                  bold: true,
                })
              );
            }
          });
          results.push(summaryRow);
        }
      }

      return {
        results,
        merges,
      };
    } catch {
      setError(true);
      return { results: [], merges: [] } as {
        results: CellObject[][];
        merges?: string[][];
      };
    }
  };

  const onExportExcel = async () => {
    setLoading(true);
    setPercent(0);
    onToggle();
    setError(false);

    if (!configs) {
      setLoading(false);
      return;
    }

    try {
      const workbook = utils.book_new();
      const dataExcel = await fetchAllData({});
      const ws = utils.aoa_to_sheet(generateHeader({ configs }));
      utils.sheet_add_aoa(ws, dataExcel?.results, {
        origin: -1,
      });

      utils.book_append_sheet(workbook, ws, fileName);

      ws["!cols"] = [...Array(500).fill({ width: colWidth })];

      writeFile(workbook, `${fileName}.xlsx`, {
        bookType: "xlsx",
        type: "buffer",
      });

      onToggle();
    } catch {
      setError(true);
    }

    setLoading(false);
  };

  const onExportWithGroupColumns = async ({
    groupColumns,
    headerFileColumns,
    merges,
    groupField,
  }: {
    groupColumns: CellObject[];
    headerFileColumns?: CellObject[][];
    merges: any;
    groupField?: {
      value: string;
      label: string;
      nested: {
        value: string;
        label: string;
      };
    };
  }) => {
    setLoading(true);
    setPercent(0);
    onToggle();
    setError(false);

    if (!configs) {
      setLoading(false);
      return;
    }

    try {
      const workbook = utils.book_new();
      const dataExcel = await fetchAllData({ groupField, startRowData: 5 });
      const ws = utils.aoa_to_sheet(
        [
          ...((headerFileColumns || undefined) as never),
          groupColumns,
          ...generateHeader({
            configs,
            bgColor: "F9FAFB",
            color: "000000",
            border: true,
            initValue: [],
          }),
        ]?.filter(Boolean)
      );
      utils.sheet_add_aoa(ws, dataExcel?.results, {
        origin: -1,
      });

      utils.book_append_sheet(workbook, ws, sheetName || fileName);

      ws["!cols"] = [{ width: 7 }, ...Array(500).fill({ width: colWidth })];
      ws["!merges"] = [
        ...merges,
        ...(getMergesXlsx(dataExcel?.merges as never) || []),
      ];
      ws["!rows"] = headerFileColumns
        ? getHeightsXlsx([30, 30, 10, 30])
        : getHeightsXlsx([30, 30]);

      writeFile(workbook, `${fileName}.xlsx`, {
        bookType: "xlsx",
        type: "buffer",
      });

      onToggle();
    } catch {
      setError(true);
    }

    setLoading(false);
  };

  const onFetchData = async () => {
    setLoading(true);
    setPercent(0);
    onToggle();
    setError(false);

    try {
      const data = await fetchAllData({});
      setLoading(false);

      return data;
    } catch {
      setError(true);
      return [];
    }
  };

  const onFetchAllRawData = async () => {
    try {
      let hasMore = true;
      let page = PAGE_DEFAULT;
      let finalRes: TItem[] = [];
      onToggle();

      while (hasMore) {
        const res = await serviceFn({
          ...filters,
          page,
          limit,
        });
        if (res?.data?.items?.length < limit) {
          hasMore = false;
        }
        finalRes = [...finalRes, ...(res?.data?.items || [])];
        const totalRecords = res?.data?.total || 0;
        setPercent(Math.round((finalRes.length / totalRecords) * 100) || 0);
        page += 1;
      }
      onToggle();
      return finalRes;
    } catch {
      setError(true);
      onToggle();
      return [];
    }
  };

  const ExportModalComponent = useMemo(() => {
    return (
      <CommonDialog
        title={titleModal || i18n.t("export")}
        name="ExportDialog"
        dialogProps={{ maxWidth: "md" }}
      >
        <Stack width={rem(500)}>
          <Typography fontWeight={400} type="TextXS" color="grey.600">
            {percent} %
          </Typography>
          <BarProgress value={percent} color={error ? "error" : "primary"} />
        </Stack>
      </CommonDialog>
    );
  }, [error, percent, titleModal]);

  return {
    ExportModalComponent,
    loading,
    onExportExcel,
    onExportWithGroupColumns,
    onFetchData,
    onFetchAllRawData,
  };
};

export default useExportExcel;
